import React, { Suspense, lazy } from 'react'
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useParams,
} from 'react-router-dom'

// Оболочка - из барреля, и это осознанно. Импорт напрямую из папок ломает
// порядок инициализации: внутри components есть цикл index -> layout -> sidebar
// -> index, и барель-как-точка-входа его гасит. Выигрыша в размере прямой
// импорт всё равно не даёт - замерено, те же 668 КБ.
import { Layout, ProtectedRoute, SettingsLayout } from './components'
import Login from './pages/login'
import RouteFallback from './components/route-fallback'

// Страницы грузятся по требованию, а не одним куском на старте.
//
// Импорт идёт из конкретной папки страницы, а НЕ из бочки './pages': бочка
// тянет за собой все 32 страницы сразу, и разбиение не состоится - webpack
// просто положит всё в один чанк.
//
// Логин оставлен обычным импортом: это первый экран для неавторизованного,
// и отдельный запрос перед ним дал бы пустой кадр на ровном месте.
const Register = lazy(() => import('./pages/register'))
const FleetList = lazy(() => import('./pages/fleet-list'))
const DriversList = lazy(() => import('./pages/drivers-list'))
const RatesList = lazy(() => import('./pages/rates-list'))
const Rate = lazy(() => import('./pages/rate'))
const RentsList = lazy(() => import('./pages/rents-list'))
const Rent = lazy(() => import('./pages/rent'))
const FleetMap = lazy(() => import('./pages/fleet-map'))
const Requests = lazy(() => import('./pages/requests'))
const Marketplace = lazy(() => import('./pages/marketplace'))
const Warehouse = lazy(() => import('./pages/warehouse'))
const ServicePrice = lazy(() => import('./pages/service-price'))
const WorkOrders = lazy(() => import('./pages/work-orders'))
const Tasks = lazy(() => import('./pages/tasks'))
const PublicWorkOrder = lazy(() => import('./pages/public-work-order'))
const Request = lazy(() => import('./pages/request'))
const Driver = lazy(() => import('./pages/driver'))
const Unit = lazy(() => import('./pages/unit'))
const ClientHubsList = lazy(() => import('./pages/client-hubs-list'))
const ClientHub = lazy(() => import('./pages/client-hub'))
const PaymentsList = lazy(() => import('./pages/payments-list'))
const Payment = lazy(() => import('./pages/payment'))
const InvitationsList = lazy(() => import('./pages/invitations-list'))
const InvitationsCouriersList = lazy(() => import('./pages/invitations-couriers-list'))
const TelegramChatsList = lazy(() => import('./pages/telegram-chats-list'))
const TelegramChat = lazy(() => import('./pages/telegram-chat'))
const Settings = lazy(() => import('./pages/settings'))
const PhoneVerification = lazy(() => import('./pages/phone-verification'))
const GeofenceEditor = lazy(() => import('./pages/geofence-editor'))
const GeofencesList = lazy(() => import('./pages/geofences-list'))
const ExternalDriver = lazy(() => import('./pages/external-driver'))
const CompaniesList = lazy(() => import('./pages/companies-list'))
const CompanyIntegrationsList = lazy(() => import('./pages/company-integrations-list'))
const CompanyPaymentMethods = lazy(() => import('./pages/company-payment-methods'))
const ContractTemplatesList = lazy(() => import('./pages/contract-templates-list'))
const DesignSystemAnalytics = lazy(() => import('./pages/design-system-analytics'))
const Subscription = lazy(() => import('./pages/subscription'))
const Staff = lazy(() => import('./pages/staff'))
const StaffInvite = lazy(() => import('./pages/staff-invite'))
// Страница устройства SP-IoT - на неё ведёт QR на плате
const IotDevice = lazy(() => import('./pages/iot-device'))

const TelegramChatRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/settings/telegram/${id}`} replace />
}

const ClientHubRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/settings/client-hubs/${id}`} replace />
}

const GeofenceRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/settings/geofences/${id}`} replace />
}

const App = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignSystemAnalytics />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/fleet"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="map"
            element={
              <ProtectedRoute>
                <FleetMap />
              </ProtectedRoute>
            }
          />
          <Route
            path="list"
            element={
              <ProtectedRoute>
                <FleetList />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/drivers"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="list"
            element={
              <ProtectedRoute>
                <DriversList />
              </ProtectedRoute>
            }
          />
          <Route
            path="integrations/:integrationId/external-drivers/:driverId"
            element={
              <ProtectedRoute>
                <ExternalDriver />
              </ProtectedRoute>
            }
          />
          <Route
            path=":id"
            element={
              <ProtectedRoute>
                <Driver />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/rates"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="list"
            element={
              <ProtectedRoute>
                <Navigate to="/settings/rates" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path=":id"
            element={
              <ProtectedRoute>
                <Rate />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/rents"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="list"
            element={
              <ProtectedRoute>
                <RentsList />
              </ProtectedRoute>
            }
          />
          <Route
            path=":id"
            element={
              <ProtectedRoute>
                <Rent />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/invitations"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="list"
            element={
              <ProtectedRoute>
                <InvitationsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="couriers"
            element={
              <ProtectedRoute>
                <InvitationsCouriersList />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/payments"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="list"
            element={
              <ProtectedRoute>
                <PaymentsList />
              </ProtectedRoute>
            }
          />
          <Route
            path=":id"
            element={
              <ProtectedRoute>
                <Payment />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/client-hubs"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="list"
            element={
              <ProtectedRoute>
                <Navigate to="/settings/client-hubs" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path=":id"
            element={
              <ProtectedRoute>
                <ClientHubRedirect />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/requests"
          element={
            <ProtectedRoute>
              <Layout>
                <Requests />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace"
          element={
            <ProtectedRoute>
              <Layout>
                <Marketplace />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/warehouse"
          element={
            <ProtectedRoute>
              <Layout>
                <Warehouse />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-price"
          element={
            <ProtectedRoute>
              <Layout>
                <ServicePrice />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <Layout>
                <Tasks />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/work-orders"
          element={
            <ProtectedRoute>
              <Layout>
                <WorkOrders />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Страница клиента: без входа и без оболочки админки */}
        <Route path="/order/:token" element={<PublicWorkOrder />} />
        {/* Регистрация сотрудника по ссылке от владельца: тоже без входа */}
        <Route path="/staff-invite" element={<StaffInvite />} />
        <Route
          path="/staff"
          element={
            <ProtectedRoute>
              <Layout>
                <Staff />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/request/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <Request />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/iot/:uid"
          element={
            <ProtectedRoute>
              <Layout>
                <IotDevice />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/unit/:id/:tab"
          element={
            <ProtectedRoute>
              <Layout>
                <Unit />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route element={<SettingsLayout />}>
            <Route
              index
              element={
                <ProtectedRoute>
                  <Navigate to="profile" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            {/* Telegram/Notifications */}
            <Route path="telegram">
              <Route
                index
                element={
                  <ProtectedRoute>
                    <TelegramChatsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute>
                    <TelegramChat />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Client hubs */}
            <Route path="client-hubs">
              <Route
                index
                element={
                  <ProtectedRoute>
                    <ClientHubsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute>
                    <ClientHub />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Geofences */}
            <Route path="geofences">
              <Route
                index
                element={
                  <ProtectedRoute>
                    <GeofencesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <ProtectedRoute>
                    <GeofenceEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute>
                    <GeofenceEditor />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Подписка: тариф, расход за месяц, счета */}
            <Route
              path="subscription"
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              }
            />

            {/* Companies */}
            <Route path="companies">
              <Route
                index
                element={
                  <ProtectedRoute>
                    <CompaniesList />
                  </ProtectedRoute>
                }
              />

              <Route
                path=":companyId/integrations"
                element={
                  <ProtectedRoute>
                    <CompanyIntegrationsList />
                  </ProtectedRoute>
                }
              />

              <Route
                path=":companyId/payment-methods"
                element={
                  <ProtectedRoute>
                    <CompanyPaymentMethods />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Payment methods for the sidebar-selected company */}
            <Route
              path="payment-methods"
              element={
                <ProtectedRoute>
                  <CompanyPaymentMethods />
                </ProtectedRoute>
              }
            />

            {/* Rates list */}
            <Route
              path="rates"
              element={
                <ProtectedRoute>
                  <RatesList />
                </ProtectedRoute>
              }
            />
            <Route
              path="contract-templates"
              element={
                  <ProtectedRoute>
                      <ContractTemplatesList />
                  </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        <Route
          path="/design-system/analytics"
          element={
            <ProtectedRoute>
              <Layout>
                <DesignSystemAnalytics />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/phone-verification"
          element={
            <ProtectedRoute>
              <PhoneVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/telegram/list"
          element={
            <ProtectedRoute>
              <Navigate to="/settings/telegram" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/telegram/:id"
          element={
            <ProtectedRoute>
              <TelegramChatRedirect />
            </ProtectedRoute>
          }
        />

        <Route
          path="/geofences"
          element={
            <ProtectedRoute>
              <Navigate to="/settings/geofences" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/geofences/new"
          element={
            <ProtectedRoute>
              <Navigate to="/settings/geofences/new" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/geofences/:id"
          element={
            <ProtectedRoute>
              <GeofenceRedirect />
            </ProtectedRoute>
          }
        />

        <Route path="/" element={<Login />} />
        <Route path="/register" element={<Register />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
